// Rolling ETA estimator for a 0→100 progress signal. Extrapolates the remaining
// time from the rate of progress, EMA-smoothed so a bursty source (network, a
// steppy encoder) doesn't make the estimate jump around. Framework-agnostic —
// callers wrap the returned `etaMs` however they like (ref, reactive map, etc).
//
//   const eta = createEtaEstimator({ minSampleMs: 5000 });
//   eta.push(42);        // -> etaMs (or null until it has two usable samples)
//   eta.reset();         // clear between uploads / on step change
//
// Options:
//   smoothing   0..1 EMA weight of the newest rate (lower = smoother, slower to
//               react). 1 = raw two-point rate, no smoothing.
//   minSampleMs ignore samples closer than this so frequent, noisy progress
//               events don't dominate; the last etaMs is carried forward.
export function createEtaEstimator({ smoothing = 0.25, minSampleMs = 0, now = () => Date.now() } = {}) {
    let sample = null; // { t, p }
    let rate = null;   // smoothed % per ms
    let etaMs = null;

    const push = (progress) => {
        const p = typeof progress === 'string' ? parseFloat(progress) : progress;
        if (!Number.isFinite(p)) return etaMs;

        const t = now();
        if (!sample) {
            sample = { t, p };
            return etaMs;
        }

        const dt = t - sample.t;
        if (dt < minSampleMs) return etaMs;

        // Only forward motion updates the rate; a flat/backward tick would spike
        // the ETA, so we keep the previous estimate instead.
        if (p > sample.p) {
            const instant = (p - sample.p) / dt; // % per ms
            rate = rate == null ? instant : smoothing * instant + (1 - smoothing) * rate;
            if (rate > 0) etaMs = (100 - p) / rate;
        }
        sample = { t, p };
        return etaMs;
    };

    const reset = () => {
        sample = null;
        rate = null;
        etaMs = null;
    };

    return { push, reset, get etaMs() { return etaMs; } };
}
